'use client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Heading, Icon, Text } from 'glic-ui-components/esm';
import { faExclamationCircle } from 'glic-ui-components/icons';
import { signIn } from 'next-auth/react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  useFormField,
} from '@/components/ui/form';
import { quote } from '@/state/quote';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { plans as plansAtom } from '@/state/plans';

import { useRouter } from 'next/navigation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { enrollment } from '@/state/enrollment';
import { sendGTMEvent } from '@next/third-parties/google';
import { attestationAtom } from '@/state/attestation';
import { Link } from './ui/link';

const formSchema = z.object({ agentId: z.string() });

const INVALID_WRITING_CODE_MSG = 'We could not recognize this writing code.';

function LoginErrorMessage() {
  const { error, formMessageId } = useFormField();

  if (!error) return null;

  return (
    <div id={formMessageId} className="flex items-start gap-3 text-error">
      <Icon
        icon={faExclamationCircle}
        size={20}
        currentColorFill
        decorative
        className="shrink-0"
      />
      {error.message === INVALID_WRITING_CODE_MSG ? (
        <Text bodyMd color="Inherit">
          <strong>We could not recognize this writing code.</strong>
          <br />
          Please enter the full Guardian writing code provided to you. Do not
          use your agency name, personal name, email address, or alternate ID.
        </Text>
      ) : (
        <Text bodyMd color="Inherit">{error.message}</Text>
      )}
    </div>
  );
}

export default function LoginForm() {
  const resetPlans = useSetAtom(plansAtom);

  const resetEnrollment = useSetAtom(enrollment);
  const resetAttestation = useSetAtom(attestationAtom);
  const resetQuote = useSetAtom(quote);
  const {
    hideAgentVerification,
    requestAgentAppointment,
    quickQuoteModule,
    oktaAuthentication,
    multiProduct,
  } = useFlags();
  const { push } = useRouter();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { agentId: '' },
  });

  async function onSubmit({ agentId }: z.infer<typeof formSchema>) {
    form.clearErrors();
    await signIn('mip-auth', { agentId, redirect: false })
      .then((response) => {
        if (response?.error) {
          form.setError('agentId', {
            type: 'manual',
            message: response.error,
          });
          sendGTMEvent({
            event: 'custom_event',
            event_name: 'agent_id_not_found',
          });
        } else {
          sendGTMEvent({
            event: 'custom_event',
            event_name: 'agent_verification_success',
          });
          push(multiProduct ? '/multi-product/quote' : '/quote');
          sessionStorage.clear();
          resetPlans(RESET);
          resetQuote(RESET);
          resetEnrollment(RESET);
          resetAttestation(false);
        }
      })
      .catch((reason) => {
        console.error('Failed to authenticate user', reason);
        sendGTMEvent({
          event: 'custom_event',
          event_name: 'agent_verification_failed',
        });
        form.setError('agentId', {
          type: 'manual',
          message: 'Failed to authenticate user, please try again',
        });
      });
  }

  function handleOktaSignIn() {
    signIn('okta', { callbackUrl: '/dashboard' }).catch((error) => {
      console.error('Okta sign-in failed:', error);
      form.setError('agentId', {
        type: 'manual',
        message: 'Failed to authenticate user, please try again',
      });
    });
  }

  return (
    <div className="w-[460px] flex flex-col gap-3">
      <Heading h1 xl>
        Welcome
      </Heading>
      <Text bodyMd className="mb-7">
        Welcome to the verification page for appointed brokers selling Guardian
        individual accident and health products. Enter the full Guardian writing
        code provided to you. Do not enter your agency name, personal name,
        email, or producer ID.
      </Text>
      <div className="flex flex-wrap justify-center content-center">
        {!hideAgentVerification && (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="p-8 w-[460px] border-[1.5px] rounded-lg bg-white border-gray-100 flex flex-col gap-y-7 shadow-[#2b2b2b]/8 shadow-light"
            >
              <FormField
                control={form.control}
                name="agentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="body-md font-bold text-blue-900">
                      Writing code
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="mt-3"
                        placeholder="Enter your 8 or 13 digit Writing code"
                        {...field}
                      />
                    </FormControl>
                    <LoginErrorMessage />
                  </FormItem>
                )}
              />
              {oktaAuthentication ? (
                <Button
                  type="button"
                  onClick={handleOktaSignIn}
                  className="w-full mt-2"
                >
                  Sign In
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  className="w-full"
                >
                  Verify Writing code
                </Button>
              )}

              {quickQuoteModule && (
                <>
                  <hr className="border-olive-400" />
                  <Link
                    href="/quick-quote"
                    className="font-bold underline text-center text-blue-900 w-full"
                    data-ta="quick-quote"
                  >
                    Get a quick quote
                  </Link>
                </>
              )}
              {requestAgentAppointment && (
                <FormLabel className="body-md font-bold text-blue-900 text-center">
                  Need to get appointed to sell Guardian individual accident and
                  health products?{' '}
                  <Link
                    href={'/request-appointment'}
                    className="font-bold underline"
                    data-ta="request-agent-appointment"
                  >
                    Click here
                  </Link>
                  .
                </FormLabel>
              )}
            </form>
          </Form>
        )}
      </div>
    </div>
  );
}
